export interface Transport {
  id: string;
  name: string;
  protocol: string;
  bind: string | null;
  local_net: string | null;
  external_media_address: string | null;
  external_signaling_address: string | null;
  external_signaling_port: number;
  tos: string | null;
  cos: number;
  allow_reload: boolean;
  allow_wildcard_certs: boolean;
  symmetric_transport: boolean;
  domain: string | null;
  cert_file: string | null;
  priv_key_file: string | null;
  ca_list_file: string | null;
  cipher: string | null;
  method: string | null;
  password: string | null;
  verify_client: string | null;
  verify_server: string | null;
  require_client_cert: boolean;
  tcp_keepalive_enable: boolean;
  tcp_keepalive_idle_time: number;
  tcp_keepalive_interval_time: number;
  tcp_keepalive_probe_count: number;
  websocket_write_timeout: number;
  created_at: string;
  updated_at: string;
}

export type TransportCreate = Omit<Transport, "id" | "created_at" | "updated_at">;
export type TransportUpdate = Partial<TransportCreate>;

export interface Auth {
  id: string;
  name: string;
  auth_type: string;
  username: string;
  password: string;
  password_digest: string | null;
  created_at: string;
  updated_at: string;
}

export type AuthCreate = Omit<Auth, "id" | "created_at" | "updated_at">;
export type AuthUpdate = Partial<AuthCreate>;

export interface Aor {
  id: string;
  name: string;
  max_contacts: number;
  minimum_expiration: number;
  maximum_expiration: number;
  qualify_frequency: number;
  qualify_timeout: number;
  qualify_2xx_only: boolean;
  authenticate_qualify: boolean;
  remove_existing: boolean;
  remove_unavailable: boolean;
  support_path: boolean;
  contact: string[];
  outbound_proxy: string | null;
  mailboxes: string | null;
  created_at: string;
  updated_at: string;
}

export type AorCreate = Omit<Aor, "id" | "created_at" | "updated_at">;
export type AorUpdate = Partial<AorCreate>;

export interface Endpoint {
  id: string;
  name: string;
  context_id: string;
  transport_id: string | null;
  disallow: string | null;
  allow: string | null;
  dtmf_mode: string;
  timers: string;
  timers_min_se: number;
  timers_sess_expires: number;
  direct_media: boolean;
  direct_media_method: string;
  force_rport: boolean;
  rewrite_contact: boolean;
  rtp_symmetric: boolean;
  rtp_ipv6: boolean;
  ice_support: boolean;
  media_address: string | null;
  media_encryption: string;
  callerid: string | null;
  callerid_privacy: string;
  from_user: string | null;
  from_domain: string | null;
  trust_id_inbound: boolean;
  trust_id_outbound: boolean;
  send_pai: boolean;
  send_rpid: boolean;
  language: string | null;
  moh_suggest: string;
  accountcode: string | null;
  mailboxes: string | null;
  acl: string | null;
  deny: string | null;
  permit: string | null;
  t38_udptl: boolean;
  t38_udptl_ec: string;
  fax_detect: boolean;
  auth_ids: string[];
  outbound_auth_ids: string[];
  aor_ids: string[];
  identify_ids: string[];
  created_at: string;
  updated_at: string;
}

export interface EndpointCreate {
  name: string;
  context_id: string;
  transport_id: string;
  disallow: string | null;
  allow: string | null;
  dtmf_mode: string;
  timers: string;
  timers_min_se: number;
  timers_sess_expires: number;
  direct_media: boolean;
  direct_media_method: string;
  force_rport: boolean;
  rewrite_contact: boolean;
  rtp_symmetric: boolean;
  rtp_ipv6: boolean;
  ice_support: boolean;
  media_address: string | null;
  media_encryption: string;
  callerid: string | null;
  callerid_privacy: string;
  from_user: string | null;
  from_domain: string | null;
  trust_id_inbound: boolean;
  trust_id_outbound: boolean;
  send_pai: boolean;
  send_rpid: boolean;
  language: string | null;
  moh_suggest: string;
  accountcode: string | null;
  mailboxes: string | null;
  acl: string | null;
  deny: string | null;
  permit: string | null;
  t38_udptl: boolean;
  t38_udptl_ec: string;
  fax_detect: boolean;
  auth_ids: string[];
  outbound_auth_ids: string[];
  aor_ids: string[];
  identify_ids: string[];
}

export type EndpointUpdate = Partial<EndpointCreate>;

export interface Identify {
  id: string;
  name: string;
  endpoint_id: string | null;
  match: string | null;
  match_header: string | null;
  match_request_uri: string | null;
  srv_lookups: boolean;
  created_at: string;
  updated_at: string;
}

export type IdentifyCreate = Omit<Identify, "id" | "created_at" | "updated_at">;
export type IdentifyUpdate = Partial<IdentifyCreate>;

export interface DialplanContext {
  id: string;
  name: string;
  description: string | null;
  include_context: string | null;
  created_at: string;
  updated_at: string;
}

export type DialplanContextCreate = Omit<DialplanContext, "id" | "created_at" | "updated_at">;
export type DialplanContextUpdate = Partial<DialplanContextCreate>;

export interface AriApp {
  id: string;
  name: string;
  password: string;
  allowed_origins: string;
  description: string | null;
  created_at: string;
  updated_at: string;
}

export type AriAppCreate = Omit<AriApp, "id" | "created_at" | "updated_at">;
export type AriAppUpdate = Partial<AriAppCreate>;

export interface WebsocketEndpoint {
  id: string;
  name: string;
  uri: string;
  protocols: string;
  connection_type: string;
  connection_timeout: number;
  reconnect_interval: number;
  reconnect_attempts: number;
  tls_enabled: boolean;
  headers: Record<string, string> | null;
  description: string | null;
  created_at: string;
  updated_at: string;
}

export type WebsocketEndpointCreate = Omit<WebsocketEndpoint, "id" | "created_at" | "updated_at">;
export type WebsocketEndpointUpdate = Partial<WebsocketEndpointCreate>;

export type ActionType =
  | "answer" | "hangup" | "dial" | "voicemail" | "set" | "goto"
  | "stasis" | "playback" | "noop" | "verbose" | "wait" | "queue"
  | "mixmonitor" | "return" | "bridge" | "echo" | "record" | "sipaddheader" | "senddtmf";

export interface ActionCreate {
  priority: number;
  type: ActionType;
  params: Record<string, unknown>;
}

export interface ActionResponse {
  id: string;
  priority: number;
  application: string;
  params: Record<string, unknown>;
  label: string | null;
}

export interface DialplanExtension {
  id: string;
  context_id: string;
  extension: string;
  actions: ActionResponse[];
  created_at: string;
  updated_at: string;
}

export interface DialplanExtensionCreate {
  context_id: string;
  extension: string;
  actions: ActionCreate[];
}

export type DialplanExtensionUpdate = Partial<{
  extension: string;
  actions: ActionCreate[];
}>;

export type ModuleName = "all" | "pjsip" | "extensions" | "ari" | "http" | "websocket";
export type ReloadModuleName = "all" | "pjsip" | "dialplan" | "ari" | "websocket";

export interface GenerateResult {
  status: string;
  results: Record<string, { path: string; sections: number }>;
}

export interface ReloadResult {
  status: string;
  results: Record<string, { success: boolean; message: string }>;
}

export const CODECS = ["ulaw", "alaw", "g722", "g729", "gsm", "opus", "h264", "vp8", "vp9"] as const;


export interface ActiveCall {
  channel: string;
  unique_id: string;
  linked_id: string | null;
  caller_id_num: string | null;
  caller_id_name: string | null;
  connected_line_num: string | null;
  connected_line_name: string | null;
  state: string | null;
  duration_seconds: number | null;
  jitter_rx: number | null;
  jitter_tx: number | null;
  packets_lost_rx: number | null;
  packets_lost_tx: number | null;
  rtt_seconds: number | null;
}

export interface CallLog {
  id: string;
  unique_id: string | null;
  linked_id: string | null;
  channel: string | null;
  caller_id_num: string | null;
  caller_id_name: string | null;
  connected_line_num: string | null;
  connected_line_name: string | null;
  direction: string | null;
  state: string | null;
  start_time: string | null;
  answer_time: string | null;
  end_time: string | null;
  duration_seconds: number | null;
  billable_seconds: number | null;
  hangup_cause: number | null;
  hangup_cause_txt: string | null;
  endpoint_name: string | null;
  context_name: string | null;
  dial_status: string | null;
  invite_headers: Record<string, string> | null;
  timestamp: string;
}

export interface OriginateCallRequest {
  caller_type: "extension" | "endpoint";
  caller_ext?: string;
  caller_context?: string;
  caller_endpoint?: string;
  dest_type: "extension" | "trunk" | "sip_uri";
  dest_ext?: string;
  dest_trunk?: string;
  dest_number?: string;
  dest_uri?: string;
  caller_name?: string;
  timeout?: number;
}

export interface AuditLog {
  id: string;
  table_name: string;
  record_id: string;
  record_name: string | null;
  operation: string;
  old_values: Record<string, unknown> | null;
  new_values: Record<string, unknown> | null;
  changed_fields: string[] | null;
  ip_address: string | null;
  user_agent: string | null;
  endpoint: string | null;
  timestamp: string;
}

export interface ServerStatus {
  public_ip: string | null;
  public_url: string | null;
  dns_updated: boolean | null;
}

export interface ConfigViewResponse {
  module: string;
  path: string;
  content: string;
}

export interface AmiCommandResponse {
  command: string;
  success: boolean;
  output: string;
}

export interface ApiKey {
  id: string;
  name: string;
  key_prefix: string;
  revoked: boolean;
  created_at: string;
  last_used_at: string | null;
}

export interface ApiKeyCreate {
  name: string;
}

export interface ApiKeyCreated extends ApiKey {
  api_key: string;
}
